/**
 * Página de Supervisión en Tiempo Real V2
 * Sistema completo de supervisión para instructores
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supervisionV2API, supervisionAPI } from '../../api';
import api from '../../api';
import useSupervisionWebSocketV2, { ConnectionState } from '../../hooks/useSupervisionWebSocketV2';
import { useVitalsTransition } from '../../hooks/useVitalsTransition';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Slider } from '../../components/ui/slider';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import useXRMirror from '../../xr/hooks/useXRMirror';
import { 
  Eye, 
  EyeOff,
  Monitor,
  MessageSquare,
  Send,
  Maximize2,
  Minimize2,
  Play,
  Pause,
  Hand,
  Lightbulb,
  AlertTriangle,
  RefreshCw,
  Users,
  Activity,
  Heart,
  Thermometer,
  Wind,
  Droplets,
  Grid3X3,
  X,
  Wifi,
  WifiOff,
  Clock,
  User,
  Building,
  GraduationCap,
  Stethoscope,
  Pill,
  FileText,
  ChevronRight,
  ChevronLeft,
  Zap,
  Brain,
  AlertCircle,
  CheckCircle,
  XCircle,
  Settings,
  Volume2,
  StickyNote,
  History,
  Target,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  LayoutGrid,
  List,
  Search,
  Filter,
  MoreVertical,
  Timer
} from 'lucide-react';
import EcgTrace from '../../components/ecg/EcgTrace';

// Constantes
const STATUS_COLORS = {
  conectado: 'bg-green-500',
  esperando_inicio: 'bg-yellow-500',
  en_simulacion: 'bg-blue-500',
  pausada: 'bg-orange-500',
  desconectado: 'bg-gray-400',
  finalizado: 'bg-purple-500'
};

const STATUS_LABELS = {
  conectado: 'Conectado',
  esperando_inicio: 'Esperando',
  en_simulacion: 'En Simulación',
  pausada: 'Pausada',
  desconectado: 'Desconectado',
  finalizado: 'Finalizado'
};

const ALERT_COLORS = {
  normal: 'border-green-200 bg-green-50',
  alerta: 'border-yellow-200 bg-yellow-50',
  critico: 'border-red-200 bg-red-50',
  emergencia: 'border-red-500 bg-red-100 animate-pulse'
};

// Componente de tarjeta de estudiante
const StudentCard = ({ student, isSelected, onSelect, onSupervise }) => {
  const alertLevel = student.alert_level || 'normal';
  
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      } ${ALERT_COLORS[alertLevel] || ''}`}
      onClick={() => onSelect(student)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
              {student.nombre?.charAt(0)}{student.apellido?.charAt(0)}
            </div>
            <div>
              <h3 className="font-medium text-sm">{student.nombre} {student.apellido}</h3>
              <p className="text-xs text-gray-500">{student.email}</p>
            </div>
          </div>
          <Badge 
            className={`${STATUS_COLORS[student.simulation_status]} text-white text-xs`}
          >
            {STATUS_LABELS[student.simulation_status]}
          </Badge>
        </div>
        
        {/* Info de institución */}
        <div className="text-xs text-gray-500 space-y-1 mb-3">
          {student.institution && (
            <div className="flex items-center gap-1">
              <Building size={12} />
              <span>{student.institution.nombre}</span>
            </div>
          )}
          {student.carrera && (
            <div className="flex items-center gap-1">
              <GraduationCap size={12} />
              <span>{student.carrera}</span>
            </div>
          )}
          {student.courses?.length > 0 && (
            <div className="flex items-center gap-1">
              <FileText size={12} />
              <span>{student.courses[0].nombre}</span>
            </div>
          )}
        </div>
        
        {/* Escenario activo */}
        {student.scenario && (
          <div className="bg-white rounded-lg p-2 mb-3 border">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Stethoscope size={14} className="text-blue-500" />
              <span className="text-xs font-medium">{student.scenario.nombre}</span>
              {(() => {
                const st = student.scenario.scenario_type || 'clinical';
                const meta = {
                  clinical:    { label: 'Clínico',    cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
                  soft_skills: { label: 'H. Blandas', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
                  hybrid:      { label: 'Híbrido',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
                }[st] || { label: st, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
                return (
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold border uppercase tracking-wide ${meta.cls}`}
                    data-testid={`supervision-type-${student.id}`}
                  >
                    {meta.label}
                  </span>
                );
              })()}
            </div>
            {student.session && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock size={12} />
                <span>{Math.floor((student.session.tiempo_transcurrido || 0) / 60)} min</span>
                <span className="text-gray-300">|</span>
                <span>Rol: {student.session.clinical_role}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Signos vitales si está en simulación */}
        {student.vitals && student.simulation_status === 'en_simulacion' && (
          <div className="grid grid-cols-4 gap-1 mb-3">
            <div className="text-center p-1 bg-red-50 rounded">
              <Heart size={12} className="mx-auto text-red-500" />
              <span className="text-xs font-mono">{student.vitals.fc || '--'}</span>
            </div>
            <div className="text-center p-1 bg-blue-50 rounded">
              <Activity size={12} className="mx-auto text-blue-500" />
              <span className="text-xs font-mono">{student.vitals.pa_sistolica || '--'}/{student.vitals.pa_diastolica || '--'}</span>
            </div>
            <div className="text-center p-1 bg-cyan-50 rounded">
              <Droplets size={12} className="mx-auto text-cyan-500" />
              <span className="text-xs font-mono">{student.vitals.spo2 || '--'}%</span>
            </div>
            <div className="text-center p-1 bg-orange-50 rounded">
              <Thermometer size={12} className="mx-auto text-orange-500" />
              <span className="text-xs font-mono">{student.vitals.temperatura || '--'}°</span>
            </div>
          </div>
        )}
        
        {/* Última acción */}
        {student.last_action && (
          <div className="text-xs text-gray-500 mb-3 p-2 bg-gray-50 rounded">
            <span className="font-medium">Última acción:</span> {student.last_action.action}
          </div>
        )}
        
        {/* Acciones */}
        {student.session && (
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="flex-1 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onSupervise(student, 'observador');
              }}
            >
              <Eye size={14} className="mr-1" />
              Observar
            </Button>
            <Button 
              size="sm" 
              className="flex-1 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onSupervise(student, 'conectarse');
              }}
              data-testid={`supervise-connect-${student.id}`}
            >
              <Monitor size={14} className="mr-1" />
              Conectar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Panel de Vitales con controles
const VitalsControlPanel = ({ vitals, patientState, hasControl, onModifyVitals, isPaused }) => {
  const [localVitals, setLocalVitals] = useState(vitals || {});
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [rhythms, setRhythms] = useState([]);
  // v10.3.1 Fase 2 — modo de aplicación
  const [applyMode, setApplyMode] = useState('immediate'); // 'immediate' | 'transition'
  const [transitionSec, setTransitionSec] = useState(15);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    api.get('/ecg-rhythms').then(r => setRhythms(r.data || [])).catch(() => {});
  }, []);
  
  useEffect(() => {
    if (vitals) setLocalVitals(vitals);
  }, [vitals]);
  
  const handleVitalChange = (key, value) => {
    setLocalVitals(prev => ({ ...prev, [key]: value }));
    setPendingChanges(prev => ({ ...prev, [key]: value }));
  };
  
  const applyChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    
    try {
      const transitionMs = applyMode === 'transition' ? transitionSec * 1000 : 0;
      await onModifyVitals({
        ...pendingChanges,
        change_type: applyMode,
        transition_duration_ms: transitionMs,
        reason: applyMode === 'transition'
          ? `Transición progresiva ${transitionSec}s`
          : 'Modificación manual del instructor'
      });
      setPendingChanges({});
      setShowConfirm(false);
      toast.success(
        applyMode === 'transition'
          ? `Transición iniciada (${transitionSec}s)`
          : 'Vitales modificados'
      );
    } catch (error) {
      toast.error('Error al modificar vitales');
    }
  };
  
  const getPatientStateColor = () => {
    switch (patientState) {
      case 'estable': return 'text-green-600 bg-green-50';
      case 'alerta': return 'text-yellow-600 bg-yellow-50';
      case 'critico': return 'text-red-600 bg-red-50';
      case 'paro': return 'text-red-800 bg-red-100 animate-pulse';
      default: return 'text-gray-600 bg-gray-50';
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-500" />
            Monitor de Signos Vitales
          </CardTitle>
          <Badge className={getPatientStateColor()}>
            {patientState || 'Sin datos'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Ritmo ECG */}
        <div className="space-y-1">
          <label className="text-xs font-medium flex items-center gap-1">
            <Activity className="w-3 h-3 text-emerald-500" />
            Ritmo ECG
          </label>
          {hasControl ? (
            <select value={localVitals.ritmo_ecg || ''} disabled={!hasControl || isPaused}
              onChange={e => handleVitalChange('ritmo_ecg', e.target.value || null)}
              className="w-full border rounded-lg px-2 py-1.5 text-xs" data-testid="instructor-rhythm-select">
              <option value="">(automático según FC)</option>
              {rhythms.map(r => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
            </select>
          ) : (
            <span className="text-xs text-slate-600">{rhythms.find(r => r.codigo === localVitals.ritmo_ecg)?.nombre || 'Automático'}</span>
          )}
        </div>
        {/* Frecuencia Cardíaca */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-xs font-medium flex items-center gap-1">
              <Heart className="w-3 h-3 text-red-500" />
              FC (lpm)
            </label>
            <span className="text-lg font-bold font-mono text-red-600">
              {localVitals.fc || '--'}
            </span>
          </div>
          {hasControl && (
            <Slider
              value={[localVitals.fc || 80]}
              onValueChange={([v]) => handleVitalChange('fc', v)}
              min={0}
              max={200}
              step={1}
              className="w-full"
              disabled={!hasControl || isPaused}
              data-testid="fc-slider"
            />
          )}
        </div>
        
        {/* Presión Arterial */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-xs font-medium flex items-center gap-1">
              <Activity className="w-3 h-3 text-blue-500" />
              PA (mmHg)
            </label>
            <span className="text-lg font-bold font-mono text-blue-600">
              {localVitals.pa_sistolica || '--'}/{localVitals.pa_diastolica || '--'}
            </span>
          </div>
          {hasControl && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-xs text-gray-500">Sistólica</span>
                <Slider
                  value={[localVitals.pa_sistolica || 120]}
                  onValueChange={([v]) => handleVitalChange('pa_sistolica', v)}
                  min={50}
                  max={250}
                  step={1}
                  disabled={!hasControl || isPaused}
                />
              </div>
              <div>
                <span className="text-xs text-gray-500">Diastólica</span>
                <Slider
                  value={[localVitals.pa_diastolica || 80]}
                  onValueChange={([v]) => handleVitalChange('pa_diastolica', v)}
                  min={30}
                  max={150}
                  step={1}
                  disabled={!hasControl || isPaused}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Saturación */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-xs font-medium flex items-center gap-1">
              <Droplets className="w-3 h-3 text-cyan-500" />
              SpO2 (%)
            </label>
            <span className={`text-lg font-bold font-mono ${
              (localVitals.spo2 || 98) < 90 ? 'text-red-600' : 'text-cyan-600'
            }`}>
              {localVitals.spo2 || '--'}%
            </span>
          </div>
          {hasControl && (
            <Slider
              value={[localVitals.spo2 || 98]}
              onValueChange={([v]) => handleVitalChange('spo2', v)}
              min={50}
              max={100}
              step={1}
              disabled={!hasControl || isPaused}
            />
          )}
        </div>
        
        {/* Frecuencia Respiratoria */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-xs font-medium flex items-center gap-1">
              <Wind className="w-3 h-3 text-green-500" />
              FR (rpm)
            </label>
            <span className="text-lg font-bold font-mono text-green-600">
              {localVitals.fr || '--'}
            </span>
          </div>
          {hasControl && (
            <Slider
              value={[localVitals.fr || 16]}
              onValueChange={([v]) => handleVitalChange('fr', v)}
              min={0}
              max={40}
              step={1}
              disabled={!hasControl || isPaused}
            />
          )}
        </div>
        
        {/* Temperatura */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-xs font-medium flex items-center gap-1">
              <Thermometer className="w-3 h-3 text-orange-500" />
              Temp (°C)
            </label>
            <span className={`text-lg font-bold font-mono ${
              (localVitals.temperatura || 37) > 38 ? 'text-red-600' : 'text-orange-600'
            }`}>
              {localVitals.temperatura?.toFixed(1) || '--'}°C
            </span>
          </div>
          {hasControl && (
            <Slider
              value={[localVitals.temperatura || 37]}
              onValueChange={([v]) => handleVitalChange('temperatura', v)}
              min={34}
              max={42}
              step={0.1}
              disabled={!hasControl || isPaused}
            />
          )}
        </div>
        
        {/* Glasgow */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-xs font-medium flex items-center gap-1">
              <Brain className="w-3 h-3 text-purple-500" />
              Glasgow
            </label>
            <span className="text-lg font-bold font-mono text-purple-600">
              {localVitals.glasgow || '--'}/15
            </span>
          </div>
          {hasControl && (
            <Slider
              value={[localVitals.glasgow || 15]}
              onValueChange={([v]) => handleVitalChange('glasgow', v)}
              min={3}
              max={15}
              step={1}
              disabled={!hasControl || isPaused}
            />
          )}
        </div>
        
        {/* v10.3.1 Fase 2: Modo de aplicación + Avanzado (señal, artefactos) */}
        {hasControl && (
          <div className="pt-3 border-t space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Modo de aplicación</label>
              <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-0.5">
                <button
                  type="button"
                  onClick={() => setApplyMode('immediate')}
                  className={`text-xs py-1.5 rounded-md font-medium transition-colors ${
                    applyMode === 'immediate' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                  }`}
                  data-testid="apply-mode-immediate"
                >Inmediato</button>
                <button
                  type="button"
                  onClick={() => setApplyMode('transition')}
                  className={`text-xs py-1.5 rounded-md font-medium transition-colors ${
                    applyMode === 'transition' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                  }`}
                  data-testid="apply-mode-transition"
                >Transición</button>
              </div>
              {applyMode === 'transition' && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">Duración</span>
                    <span className="text-xs font-mono font-semibold text-blue-600">{transitionSec}s</span>
                  </div>
                  <div className="flex gap-1">
                    {[5, 15, 30, 60, 120].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setTransitionSec(s)}
                        className={`flex-1 text-[11px] py-1 rounded border transition-colors ${
                          transitionSec === s
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : 'bg-white border-slate-300 text-slate-600 hover:border-slate-400'
                        }`}
                        data-testid={`transition-preset-${s}`}
                      >{s}s</button>
                    ))}
                  </div>
                  <Slider
                    value={[transitionSec]}
                    onValueChange={([v]) => setTransitionSec(v)}
                    min={2}
                    max={300}
                    step={1}
                    className="w-full mt-1"
                    data-testid="transition-duration-slider"
                  />
                  <p className="text-[10px] text-slate-500 leading-tight">
                    Los valores numéricos (FC, PA, SpO₂, FR) se interpolarán suavemente.
                    El ritmo ECG y la calidad de señal se aplican al inicio.
                  </p>
                </div>
              )}
            </div>

            {/* Controles avanzados */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
                data-testid="toggle-advanced-vitals"
              >
                {showAdvanced ? '▼' : '▶'} Señal, artefactos y amplitud
              </button>
              {showAdvanced && (
                <div className="mt-2 space-y-3">
                  {/* Calidad de señal */}
                  <div className="space-y-1">
                    <label className="text-[11px] text-slate-500">Calidad de señal</label>
                    <select
                      value={localVitals.signal_quality || 'excellent'}
                      disabled={isPaused}
                      onChange={(e) => handleVitalChange('signal_quality', e.target.value)}
                      className="w-full border rounded-lg px-2 py-1.5 text-xs"
                      data-testid="signal-quality-select"
                    >
                      <option value="excellent">Excelente</option>
                      <option value="good">Buena</option>
                      <option value="moderate">Moderada</option>
                      <option value="poor">Deficiente (activa artefactos)</option>
                      <option value="disconnected">Desconectado</option>
                    </select>
                  </div>

                  {/* Tipo de artefacto */}
                  <div className="space-y-1">
                    <label className="text-[11px] text-slate-500">Artefacto</label>
                    <select
                      value={localVitals.artifact_type || ''}
                      disabled={isPaused}
                      onChange={(e) => handleVitalChange('artifact_type', e.target.value || null)}
                      className="w-full border rounded-lg px-2 py-1.5 text-xs"
                      data-testid="artifact-type-select"
                    >
                      <option value="">Ninguno</option>
                      <option value="movement">Movimiento</option>
                      <option value="muscle_tremor">Temblor muscular</option>
                      <option value="electrode">Mal contacto de electrodo</option>
                      <option value="electrical">Interferencia eléctrica</option>
                    </select>
                  </div>

                  {/* Amplitud ECG */}
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <label className="text-[11px] text-slate-500">Amplitud ECG</label>
                      <span className="text-[11px] font-mono text-slate-700">{(localVitals.ecg_amplitude ?? 1).toFixed(2)}×</span>
                    </div>
                    <Slider
                      value={[Math.round((localVitals.ecg_amplitude ?? 1) * 100)]}
                      onValueChange={([v]) => handleVitalChange('ecg_amplitude', v / 100)}
                      min={20}
                      max={200}
                      step={5}
                      disabled={isPaused}
                      data-testid="ecg-amplitude-slider"
                    />
                  </div>

                  {/* Patrón respiratorio */}
                  <div className="space-y-1">
                    <label className="text-[11px] text-slate-500">Patrón respiratorio</label>
                    <select
                      value={localVitals.patron_respiratorio || 'normal'}
                      disabled={isPaused}
                      onChange={(e) => handleVitalChange('patron_respiratorio', e.target.value)}
                      className="w-full border rounded-lg px-2 py-1.5 text-xs"
                      data-testid="respiratory-pattern-select"
                    >
                      <option value="normal">Normal</option>
                      <option value="rapido_superficial">Rápido superficial</option>
                      <option value="kussmaul">Kussmaul (profunda)</option>
                      <option value="cheyne_stokes">Cheyne-Stokes</option>
                      <option value="irregular">Irregular</option>
                      <option value="apnea">Apnea</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Botón de aplicar cambios */}
        {hasControl && Object.keys(pendingChanges).length > 0 && (
          <div className="pt-2 border-t">
            <Button 
              onClick={() => setShowConfirm(true)}
              className="w-full"
              variant="destructive"
              data-testid="apply-vitals-changes-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              {applyMode === 'transition'
                ? `Iniciar Transición (${transitionSec}s) — ${Object.keys(pendingChanges).length} cambios`
                : `Aplicar Cambios (${Object.keys(pendingChanges).length})`}
            </Button>
          </div>
        )}
        
        {/* Dialog de confirmación */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Modificación de Vitales</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de aplicar estos cambios? Esto afectará inmediatamente la simulación del estudiante.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              {Object.entries(pendingChanges).map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="font-medium">{key}</span>
                  <span>{vitals?.[key] || '--'} → {value}</span>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={applyChanges} data-testid="confirm-vitals-btn">
                Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Panel de Eventos Clínicos
const ClinicalEventsPanel = ({ sessionId, scenarioId, hasControl, onActivateEvent }) => {
  const [events, setEvents] = useState({ global_events: [], scenario_events: [] });
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [customDescription, setCustomDescription] = useState('');
  
  useEffect(() => {
    loadEvents();
  }, [scenarioId]);
  
  const loadEvents = async () => {
    try {
      const response = await supervisionV2API.getAvailableEvents(scenarioId);
      setEvents(response.data);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };
  
  const handleActivate = async () => {
    if (!selectedEvent) return;
    
    setLoading(true);
    try {
      await onActivateEvent({
        event_type: selectedEvent.id,
        event_name: selectedEvent.name || selectedEvent.id,
        // Bug fix P0 (v10.2.2): pass authoritative `effects` from the event
        // definition. Without this, the backend gets an empty effects dict
        // and the student's vitals never change when the instructor
        // activates "Bradicardia", "Shock", etc.
        effects: selectedEvent.effects || selectedEvent.efectos || {},
        description: customDescription || undefined
      });
      toast.success(`Evento "${selectedEvent.name}" activado`);
      setSelectedEvent(null);
      setCustomDescription('');
    } catch (error) {
      toast.error('Error al activar evento');
    } finally {
      setLoading(false);
    }
  };
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'emergency': return 'bg-red-500 text-white';
      case 'critical': return 'bg-orange-500 text-white';
      case 'warning': return 'bg-yellow-500';
      case 'positive': return 'bg-green-500 text-white';
      default: return 'bg-gray-200';
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-500" />
          Eventos Clínicos
        </CardTitle>
        <CardDescription className="text-xs">
          Activa eventos para modificar el escenario
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px]">
          <div className="space-y-2">
            {events.global_events.map((event) => (
              <div 
                key={event.id}
                className={`p-2 rounded-lg border cursor-pointer transition-all ${
                  selectedEvent?.id === event.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelectedEvent(event)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{event.name}</span>
                  <Badge className={getSeverityColor(event.severity)} variant="secondary">
                    {event.severity}
                  </Badge>
                </div>
                <span className="text-xs text-gray-500">{event.category}</span>
              </div>
            ))}
          </div>
        </ScrollArea>
        
        {selectedEvent && hasControl && (
          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium">Activar: {selectedEvent.name}</span>
            </div>
            <Textarea
              placeholder="Descripción adicional (opcional)"
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              className="text-sm"
              rows={2}
            />
            <Button 
              onClick={handleActivate}
              disabled={loading}
              className="w-full"
              variant={selectedEvent.severity === 'emergency' ? 'destructive' : 'default'}
            >
              {loading ? 'Activando...' : 'Activar Evento'}
            </Button>
          </div>
        )}
        
        {!hasControl && (
          <div className="mt-4 p-3 bg-gray-100 rounded-lg text-center text-sm text-gray-500">
            Necesitas control total para activar eventos
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Panel de Mensajería
const MessagingPanel = ({ 
  sessionId, 
  messages, 
  hasControl, 
  supervisionMode,
  onSendMessage,
  onSendHint 
}) => {
  const [messageContent, setMessageContent] = useState('');
  const [messageType, setMessageType] = useState('privado');
  const [displayType, setDisplayType] = useState('chat');
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const handleSend = async () => {
    if (!messageContent.trim()) return;
    
    try {
      await onSendMessage({
        content: messageContent,
        message_type: messageType,
        display_type: displayType
      });
      setMessageContent('');
    } catch (error) {
      toast.error('Error al enviar mensaje');
    }
  };
  
  const canSendMessages = true; // Chat is always allowed; only interventions require modes
  
  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-blue-500" />
          Comunicación
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        {/* Mensajes */}
        <ScrollArea className="flex-1 min-h-[200px] bg-gray-50 rounded-lg p-2 mb-3">
          {messages.length === 0 ? (
            <p className="text-center text-gray-400 text-xs py-8">
              Sin mensajes
            </p>
          ) : (
            <div className="space-y-2">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`p-2 rounded-lg text-xs ${
                    msg.isHint 
                      ? 'bg-yellow-100 border-l-4 border-yellow-500'
                      : msg.sender_role === 'instructor'
                        ? 'bg-blue-100 ml-4'
                        : 'bg-white mr-4 border'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{msg.sender_name}</span>
                    {msg.isHint && (
                      <Badge className="bg-yellow-500 text-xs">Pista</Badge>
                    )}
                  </div>
                  <p className="text-gray-700">{msg.content}</p>
                  <span className="text-gray-400 text-xs">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>
        
        {/* Controles de envío */}
        {canSendMessages ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Select value={messageType} onValueChange={setMessageType}>
                <SelectTrigger className="w-[120px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="privado">Privado</SelectItem>
                  <SelectItem value="pista">Pista</SelectItem>
                  <SelectItem value="advertencia">Advertencia</SelectItem>
                  <SelectItem value="info_paciente">Info Paciente</SelectItem>
                  <SelectItem value="resultado_clinico">Resultado</SelectItem>
                </SelectContent>
              </Select>
              <Select value={displayType} onValueChange={setDisplayType}>
                <SelectTrigger className="w-[120px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chat">Chat</SelectItem>
                  <SelectItem value="popup">Popup</SelectItem>
                  <SelectItem value="notificacion">Notificación</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Input
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Escribe un mensaje..."
                className="text-xs"
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                data-testid="supervision-message-input"
              />
              <Button size="sm" onClick={handleSend} data-testid="supervision-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center text-xs text-gray-500 p-2 bg-gray-100 rounded">
            Modo observador: solo lectura
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Timeline de Actividad
const ActivityTimeline = ({ timeline, studentActions, medications, exams }) => {
  const allEvents = [
    ...(timeline || []).map(e => ({ ...e, source: 'timeline' })),
    ...(studentActions || []).map(a => ({ 
      ...a, 
      timestamp: a.timestamp || a.created_at,
      event_type: 'action',
      source: 'action'
    })),
    ...(medications || []).map(m => ({ 
      ...m, 
      timestamp: m.timestamp || m.administered_at,
      event_type: 'medication',
      source: 'medication'
    })),
    ...(exams || []).map(e => ({ 
      ...e, 
      timestamp: e.timestamp || e.ordered_at,
      event_type: 'exam',
      source: 'exam'
    }))
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  
  const getEventIcon = (event) => {
    switch (event.event_type || event.source) {
      case 'medication': return <Pill className="w-3 h-3 text-green-500" />;
      case 'exam': return <FileText className="w-3 h-3 text-blue-500" />;
      case 'clinical_event': return <AlertTriangle className="w-3 h-3 text-orange-500" />;
      case 'instructor_intervention': return <Hand className="w-3 h-3 text-purple-500" />;
      default: return <Activity className="w-3 h-3 text-gray-500" />;
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <History className="w-4 h-4 text-gray-500" />
          Cronología de Actividad
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          {allEvents.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-8">
              Sin actividad registrada
            </p>
          ) : (
            <div className="space-y-2">
              {allEvents.slice(0, 50).map((event, idx) => (
                <div 
                  key={event.id || idx}
                  className={`flex items-start gap-2 p-2 rounded text-xs ${
                    event.is_instructor_intervention 
                      ? 'bg-purple-50 border-l-2 border-purple-500'
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="mt-0.5">{getEventIcon(event)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {event.action || event.nombre || event.event_type}
                    </p>
                    {event.details && (
                      <p className="text-gray-500 truncate">
                        {typeof event.details === 'string' 
                          ? event.details 
                          : JSON.stringify(event.details).slice(0, 50)}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1 text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>
                        {event.elapsed_seconds != null 
                          ? `${Math.floor(event.elapsed_seconds / 60)}:${String(event.elapsed_seconds % 60).padStart(2, '0')}`
                          : new Date(event.timestamp).toLocaleTimeString()
                        }
                      </span>
                      {event.actor && (
                        <>
                          <User className="w-3 h-3 ml-2" />
                          <span>{event.actor}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// v10.10.0 — Enviar notificación PWA al alumno XR (aparece en el móvil
// como notificación nativa vía el Service Worker de la PWA XR).
const XRNotificationSender = ({ sessionId }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const send = async (preset) => {
    if (!sessionId) return;
    const spec = preset === 'pause' ? { title: 'Sesión en pausa', body: 'El instructor pausó la simulación.', kind: 'pause', require_interaction: true }
      : preset === 'resume' ? { title: 'Sesión reanudada', body: 'Continúa con la simulación.', kind: 'resume' }
      : preset === 'alert' ? { title: '⚠︎ Aviso importante', body: text || 'Revisa el estado del paciente.', kind: 'alert', require_interaction: true }
      : { title: 'Mensaje del instructor', body: text, kind: 'info' };
    if (spec.kind === 'info' && !text.trim()) return;
    setSending(true);
    try {
      await api.post('/xr/notifications/broadcast', { session_id: sessionId, ...spec });
      toast.success('Notificación enviada al alumno');
      if (preset === 'text' || preset === 'alert') setText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar');
    } finally { setSending(false); }
  };
  return (
    <Card className="border-cyan-200" data-testid="xr-notification-sender">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
          Enviar aviso al alumno (PWA)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-3 gap-1.5">
          <Button size="sm" variant="outline" disabled={!sessionId || sending} onClick={() => send('pause')} data-testid="xr-notify-pause-btn" className="text-[10px] h-7">Pausar</Button>
          <Button size="sm" variant="outline" disabled={!sessionId || sending} onClick={() => send('resume')} data-testid="xr-notify-resume-btn" className="text-[10px] h-7">Reanudar</Button>
          <Button size="sm" variant="outline" disabled={!sessionId || sending} onClick={() => send('alert')} data-testid="xr-notify-alert-btn" className="text-[10px] h-7 text-red-700 border-red-300">Alerta</Button>
        </div>
        <div className="flex gap-1.5">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Mensaje libre…" className="text-xs h-8" data-testid="xr-notify-text-input" />
          <Button size="sm" disabled={!sessionId || !text.trim() || sending} onClick={() => send('text')} data-testid="xr-notify-send-btn" className="h-8 bg-cyan-600 hover:bg-cyan-500 text-xs">Enviar</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// v10.11.0 — Panel Mirror WebRTC: instructor ve la cámara AR del alumno en tiempo real
const XRMirrorPanel = ({ sessionId }) => {
  const [token] = useState(() => localStorage.getItem('token') || '');
  const mirror = useXRMirror({
    role: 'instructor',
    sessionId,
    accessToken: token,
    enabled: !!sessionId && !!token,
  });
  const videoRef = React.useRef(null);
  React.useEffect(() => {
    if (videoRef.current && mirror.remoteStream) {
      videoRef.current.srcObject = mirror.remoteStream;
    }
  }, [mirror.remoteStream]);

  return (
    <Card className="border-cyan-200" data-testid="xr-mirror-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${mirror.status === 'streaming' ? 'bg-red-500 animate-pulse' : 'bg-slate-400'}`}></span>
          Cámara AR del alumno (espejo)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {mirror.status === 'streaming' && mirror.remoteStream ? (
          <div className="relative rounded overflow-hidden bg-black" data-testid="xr-mirror-video-container">
            <video ref={videoRef} autoPlay playsInline muted className="w-full aspect-video object-cover" />
            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-red-500/90 text-white text-[9px] font-bold flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-white" /> EN VIVO
            </div>
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 py-3 text-center bg-slate-50 rounded" data-testid="xr-mirror-idle">
            {mirror.status === 'signaling' ? 'Estableciendo conexión…' : 'No hay transmisión activa'}
          </div>
        )}
        {mirror.error && (
          <div className="text-[10px] text-red-600 bg-red-50 p-1.5 rounded" data-testid="xr-mirror-error">{mirror.error}</div>
        )}
        <div className="flex gap-2">
          {mirror.status !== 'streaming' && mirror.status !== 'signaling' ? (
            <Button size="sm" onClick={mirror.startViewing} disabled={!sessionId || !mirror.connected} className="flex-1 bg-cyan-600 hover:bg-cyan-500 h-8 text-xs" data-testid="xr-mirror-start-btn">
              Ver cámara del alumno
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={mirror.stopViewing} className="flex-1 h-8 text-xs" data-testid="xr-mirror-stop-btn">
              Detener transmisión
            </Button>
          )}
        </div>
        <p className="text-[10px] text-slate-400 leading-tight">
          El alumno verá un badge "🔴 Instructor viendo" cuando actives la vista.
          Requiere que el alumno tenga la cámara AR encendida.
        </p>
      </CardContent>
    </Card>
  );
};

// Componente Principal
export default function SupervisionPageV2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'mosaic' | 'individual'
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showControlPanel, setShowControlPanel] = useState(true);
  const [supervisionLimits, setSupervisionLimits] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const prevActiveSessionsRef = useRef(null);
  
  // WebSocket para la sesión activa
  const {
    connectionState,
    isConnected,
    messages,
    vitals,
    patientState,
    timeline: wsTimeline,
    liveMedications,
    liveActions,
    liveExams,
    isPaused,
    supervisionMode,
    hasControl,
    latency,
    connectedUsers,
    sendChatMessage,
    sendHint,
    takeControl,
    releaseControl,
    setMode,
    sendVitalsUpdate,
    pauseSimulation: wsPauseSimulation,
    resumeSimulation: wsResumeSimulation,
    endSimulation: wsEndSimulation,
    triggerEvent,
    requestStateSync,
    // v10.2.4 — pedidos de control del alumno
    pendingControlRequest,
    dismissControlRequest,
    // v10.3.1 Fase 2 — transición progresiva
    activeVitalsTransition,
    setActiveVitalsTransition,
    // v10.8.1 — XR live
    xrEvents,
    lastXrEvent,
    disconnect: disconnectWs
  } = useSupervisionWebSocketV2(
    activeSessionId,
    localStorage.getItem('token'),
    'instructor'
  );

  // v10.3.1 Fase 2: aplicar interpolación local para que el monitor del
  // instructor muestre la misma transición suave que el alumno.
  const displayedVitals = useVitalsTransition(vitals, activeVitalsTransition);

  // v10.5.0 regresión fix: merge del snapshot REST inicial + eventos LIVE
  // por WebSocket. El instructor ve medicamentos/acciones/exámenes tan
  // pronto el alumno los ejecuta, sin necesidad de F5.
  const mergedMedications = useMemo(() => {
    const snapshot = sessionData?.activity_area?.medications_administered || [];
    return [...snapshot, ...(liveMedications || [])];
  }, [sessionData?.activity_area?.medications_administered, liveMedications]);
  const mergedActions = useMemo(() => {
    const snapshot = sessionData?.activity_area?.actions_performed || [];
    return [...snapshot, ...(liveActions || [])];
  }, [sessionData?.activity_area?.actions_performed, liveActions]);
  const mergedExams = useMemo(() => {
    const snapshot = sessionData?.activity_area?.exams_ordered || [];
    return [...snapshot, ...(liveExams || [])];
  }, [sessionData?.activity_area?.exams_ordered, liveExams]);
  useEffect(() => {
    if (!activeVitalsTransition) return undefined;
    const dur = activeVitalsTransition.duration_ms || 5000;
    const startedAt = activeVitalsTransition.appliedAtMs || performance.now();
    const remaining = Math.max(0, dur - (performance.now() - startedAt));
    const t = setTimeout(() => setActiveVitalsTransition(null), remaining + 100);
    return () => clearTimeout(t);
  }, [activeVitalsTransition, setActiveVitalsTransition]);

  // Auto-elevate to 'asistido' mode once WS is connected (bug fix supervisión:
  // el modo por defecto era 'observador', lo que deshabilitaba mensajes y hacía
  // parecer que la comunicación no funcionaba). El instructor puede bajar a
  // 'observador' explícitamente desde el dropdown si lo desea.
  useEffect(() => {
    if (isConnected && activeSessionId && supervisionMode === 'observador') {
      setMode('asistido');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, activeSessionId]);

  // v10.2.4: cuando el alumno solicita atención / control, mostrar un toast
  // persistente con la razón + acción rápida "Liberar control" si aplica.
  useEffect(() => {
    if (!pendingControlRequest) return;
    const { student_name, reason, controller_id } = pendingControlRequest;
    const iHaveControl = hasControl;
    toast(
      <div className="flex items-start gap-3">
        <div className="p-1.5 rounded-full bg-amber-100">
          <span className="block w-4 h-4 text-lg leading-none">✋</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">
            {student_name || 'El alumno'} solicita tu atención
          </p>
          {reason && (
            <p className="text-xs text-slate-600 mt-0.5 line-clamp-3">&ldquo;{reason}&rdquo;</p>
          )}
          {controller_id && iHaveControl && (
            <button
              type="button"
              className="mt-2 text-xs text-blue-600 hover:underline font-medium"
              onClick={() => { releaseControl(); dismissControlRequest(); }}
              data-testid="notif-release-control-btn"
            >
              Liberar control ahora
            </button>
          )}
        </div>
      </div>,
      {
        duration: 12000,
        onDismiss: () => dismissControlRequest(),
        onAutoClose: () => dismissControlRequest(),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingControlRequest]);

  // Cargar estudiantes autorizados
  const loadStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }
      const response = await supervisionV2API.getAuthorizedStudents(params);
      const newStudents = response.data.students || [];
      // Fase D: notificar sesiones nuevas en tiempo real (sin recargar)
      const activeNow = new Set(newStudents.filter(s => s.simulation_status === 'en_simulacion').map(s => s.id));
      if (prevActiveSessionsRef.current) {
        newStudents.forEach(s => {
          if (s.simulation_status === 'en_simulacion' && !prevActiveSessionsRef.current.has(s.id)) {
            toast.info(`${s.nombre || ''} ${s.apellido || ''} inició una simulación`, {
              description: 'Ya puedes supervisarla en vivo',
              duration: 10000
            });
          }
        });
      }
      prevActiveSessionsRef.current = activeNow;
      setStudents(newStudents);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Error al cargar estudiantes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadStudents();
    loadLimits();
    const interval = setInterval(loadStudents, 15000); // Refresh cada 15s (Fase D: detectar sesiones nuevas)
    return () => clearInterval(interval);
  }, [loadStudents]);

  // Cargar límites de supervisión
  const loadLimits = useCallback(async () => {
    try {
      const response = await supervisionV2API.getLimits();
      setSupervisionLimits(response.data);
    } catch (error) {
      console.error('Error loading limits:', error);
    }
  }, []);

  // Cargar datos de sesión cuando se selecciona un estudiante
  const loadSessionData = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const response = await supervisionV2API.getSessionFullState(sessionId);
      setSessionData(response.data);
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Error al cargar datos de la sesión');
    }
  }, []);

  // Fase D: cuenta regresiva del tiempo de la sesión supervisada
  useEffect(() => {
    const tc = sessionData?.time_control;
    if (!tc?.expires_at) { setTimeRemaining(null); return; }
    const tick = () => {
      if (tc.is_paused || isPaused) return; // congelar durante pausa
      const remaining = Math.floor((new Date(tc.expires_at).getTime() - Date.now()) / 1000);
      setTimeRemaining(remaining);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [sessionData?.time_control, isPaused]);

  // Fase D: extender tiempo de la simulación
  const handleExtendTime = async (minutes) => {
    if (!activeSessionId) return;
    try {
      const r = await supervisionV2API.extendTime(activeSessionId, minutes);
      toast.success(r.data.message || `Tiempo extendido +${minutes} min`);
      loadSessionData(activeSessionId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al extender el tiempo');
    }
  };

  // Conectar a supervisión
  const handleSupervise = async (student, action) => {
    if (!student.session?.id) {
      toast.error('El estudiante no tiene simulación activa');
      return;
    }
    
    const sessionId = student.session.id;
    
    // Registrar supervisión en BD
    try {
      await supervisionAPI.startSupervision(sessionId, action === 'observador' ? 'observador' : 'asistido');
    } catch (error) {
      console.error('Error starting supervision:', error);
    }
    
    // Establecer sesión activa
    setActiveSessionId(sessionId);
    setSelectedStudent(student);
    setViewMode('individual');
    
    // Cargar datos completos
    loadSessionData(sessionId);
  };

  // Desconectar supervisión
  const handleDisconnect = async () => {
    if (activeSessionId) {
      try {
        await supervisionAPI.endSupervision(activeSessionId);
      } catch (error) {
        console.error('Error ending supervision:', error);
      }
    }
    
    disconnectWs();
    setActiveSessionId(null);
    setSelectedStudent(null);
    setSessionData(null);
    setViewMode('list');
  };

  // Modificar vitales - usa WebSocket si está conectado
  const handleModifyVitals = async (data) => {
    if (!activeSessionId) return;
    // v10.3.1 Fase 2: extraer transition_duration_ms y change_type del
    // payload — el resto son los campos de vitales.
    const { reason, transition_duration_ms, change_type, ...vitalsChanges } = data;
    
    // Preferir WebSocket si está conectado y tiene control
    if (isConnected && hasControl) {
      sendVitalsUpdate(vitalsChanges, reason || '', transition_duration_ms || 0);
      return;
    }
    
    // Fallback a HTTP (nota: el endpoint HTTP no soporta transición aún)
    await supervisionV2API.modifyVitals(activeSessionId, {
      ...vitalsChanges,
      reason,
      change_type,
      transition_duration_ms,
    });
  };

  // Activar evento clínico - usa WebSocket si está conectado
  const handleActivateEvent = async (data) => {
    if (!activeSessionId) return;
    
    // Preferir WebSocket si está conectado y tiene control
    if (isConnected && hasControl) {
      // Bug fix P0 (v10.2.2): usar el event_name legible + los `effects`
      // reales para que el backend pueda aplicar el merge de vitales al
      // estado_actual del paciente.
      triggerEvent(
        data.event_name || data.event_type,
        data.effects || data.effect || {}
      );
      return;
    }
    
    // Fallback a HTTP
    await supervisionV2API.activateEvent(activeSessionId, data);
  };

  // Enviar mensaje - usa WebSocket
  const handleSendMessage = async (data) => {
    if (!activeSessionId) return;
    
    // Preferir WebSocket si está conectado
    if (isConnected) {
      if (data.message_type === 'pista') {
        sendHint(data.content, data.message_type);
      } else {
        sendChatMessage(data.content);
      }
      return;
    }
    
    // Fallback a HTTP
    await supervisionV2API.sendMessage(activeSessionId, data);
  };

  // Pausar simulación - usa WebSocket
  const handlePause = async () => {
    if (!activeSessionId || !hasControl) return;
    try {
      if (isConnected) {
        wsPauseSimulation('Pausada por instructor');
        toast.success('Simulación pausada');
        return;
      }
      await supervisionV2API.pauseSimulation(activeSessionId);
      toast.success('Simulación pausada');
    } catch (error) {
      toast.error('Error al pausar');
    }
  };

  // Reanudar simulación - usa WebSocket
  const handleResume = async () => {
    if (!activeSessionId || !hasControl) return;
    try {
      if (isConnected) {
        wsResumeSimulation();
        toast.success('Simulación reanudada');
        return;
      }
      await supervisionV2API.resumeSimulation(activeSessionId);
      toast.success('Simulación reanudada');
    } catch (error) {
      toast.error('Error al reanudar');
    }
  };

  // Finalizar simulación - usa WebSocket
  const handleEndSimulation = async () => {
    if (!activeSessionId || !hasControl) return;
    if (!window.confirm('¿Seguro que deseas finalizar esta simulación?')) return;
    
    try {
      if (isConnected) {
        wsEndSimulation('Finalizada por instructor');
        toast.success('Simulación finalizada');
        handleDisconnect();
        return;
      }
      await supervisionV2API.endSimulation(activeSessionId, 'Finalizada por instructor');
      toast.success('Simulación finalizada');
      handleDisconnect();
    } catch (error) {
      toast.error('Error al finalizar');
    }
  };

  // Filtrar estudiantes
  const filteredStudents = students.filter(s => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        s.nombre?.toLowerCase().includes(query) ||
        s.apellido?.toLowerCase().includes(query) ||
        s.email?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Vista Individual de Supervisión
  if (viewMode === 'individual' && selectedStudent && sessionData) {
    return (
      <div className="h-[calc(100vh-4rem)] flex flex-col bg-gray-100">
        {/* Header */}
        <div className="bg-white border-b px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleDisconnect}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                {selectedStudent.nombre?.charAt(0)}{selectedStudent.apellido?.charAt(0)}
              </div>
              <div>
                <h2 className="font-medium text-sm">{selectedStudent.nombre} {selectedStudent.apellido}</h2>
                <p className="text-xs text-gray-500">{sessionData.scenario?.nombre}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Indicador de conexión */}
            <Badge variant={isConnected ? 'default' : 'secondary'} className="flex items-center gap-1">
              {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {connectionState}
              {latency > 0 && <span className="text-xs ml-1">({latency}ms)</span>}
            </Badge>
            
            {/* Modo de supervisión */}
            <Select value={supervisionMode} onValueChange={setMode}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="observador">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Observador
                  </div>
                </SelectItem>
                <SelectItem value="asistido">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Asistido
                  </div>
                </SelectItem>
                <SelectItem value="control_total">
                  <div className="flex items-center gap-2">
                    <Hand className="w-4 h-4" />
                    Control Total
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            {/* Control */}
            {supervisionMode === 'control_total' && (
              hasControl ? (
                <Button size="sm" variant="destructive" onClick={releaseControl} data-testid="release-control-btn">
                  <X className="w-4 h-4 mr-1" />
                  Liberar Control
                </Button>
              ) : (
                <Button size="sm" onClick={takeControl} data-testid="take-control-btn">
                  <Hand className="w-4 h-4 mr-1" />
                  Tomar Control
                </Button>
              )
            )}
            
            {/* Fase D: cuenta regresiva + extender tiempo */}
            {timeRemaining != null && (
              <Badge
                variant="outline"
                className={`gap-1 font-mono ${timeRemaining <= 60 ? 'border-red-400 text-red-600 animate-pulse' : timeRemaining <= 180 ? 'border-amber-400 text-amber-600' : 'text-slate-600'}`}
                data-testid="supervision-countdown"
              >
                <Timer className="w-3 h-3" />
                {timeRemaining > 0
                  ? `${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')}`
                  : 'Agotado'}
              </Badge>
            )}
            <Button size="sm" variant="outline" onClick={() => handleExtendTime(5)} data-testid="extend-time-btn">
              <Timer className="w-4 h-4 mr-1" />
              +5 min
            </Button>
            
            {/* Pausa/Reanudar */}
            {hasControl && (
              isPaused ? (
                <Button size="sm" variant="outline" onClick={handleResume} data-testid="resume-btn">
                  <Play className="w-4 h-4 mr-1" />
                  Reanudar
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={handlePause} data-testid="pause-btn">
                  <Pause className="w-4 h-4 mr-1" />
                  Pausar
                </Button>
              )
            )}
            
            {/* Finalizar */}
            {hasControl && (
              <Button size="sm" variant="destructive" onClick={handleEndSimulation} data-testid="end-simulation-btn">
                Finalizar
              </Button>
            )}
          </div>
        </div>
        
        {/* Contenido principal */}
        <div className="flex-1 p-4 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 h-full">
            {/* Área del Paciente y Vitales */}
            <div className="col-span-3 space-y-4 overflow-auto">
              {/* Fase D: ECG en vivo del alumno */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>ECG en vivo</span>
                    <span className="text-xs font-normal text-emerald-600">
                      {(vitals || sessionData.patient_area?.vitales)?.fc ?? '--'} lpm
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <EcgTrace
                    rhythm={(displayedVitals || vitals || sessionData.patient_area?.vitales)?.ritmo_ecg}
                    fc={(displayedVitals || vitals || sessionData.patient_area?.vitales)?.fc || 80}
                  />
                </CardContent>
              </Card>
              <VitalsControlPanel
                vitals={displayedVitals || vitals || sessionData.patient_area?.vitales}
                patientState={patientState || sessionData.patient_area?.estado_clinico}
                hasControl={hasControl}
                onModifyVitals={handleModifyVitals}
                isPaused={isPaused}
              />
              
              {/* v10.8.1 — XR Live: qué está viendo/haciendo el alumno con XR */}
              {(lastXrEvent || (xrEvents || []).length > 0) && (
                <Card className="border-cyan-200">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-sm flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></span>
                      XR Live · Alumno
                    </CardTitle>
                    <span className="text-[10px] text-slate-400">{(xrEvents || []).length} eventos</span>
                  </CardHeader>
                  <CardContent className="text-xs space-y-1.5" data-testid="xr-live-panel">
                    {lastXrEvent && (
                      <div className="p-2 rounded bg-cyan-50 border border-cyan-200">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[10px] font-bold uppercase text-cyan-600 tracking-widest">Última interacción</span>
                        </div>
                        <div className="font-mono text-cyan-700 text-[11px]">{lastXrEvent.event_type.replace('xr:', '')}</div>
                        <div className="text-slate-600 mt-0.5">
                          {Object.entries(lastXrEvent.target || {}).filter(([, v]) => v != null && v !== '').map(([k, v]) => (
                            <span key={k} className="mr-2"><strong>{k}</strong>=<code>{String(v)}</code></span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {(xrEvents || []).slice(0, 8).map((e, i) => (
                        <div key={e.event_id || i} className="flex items-center gap-2 text-[10px]">
                          <span className="text-slate-400 font-mono">#{e.sequence_number || '?'}</span>
                          <span className="font-mono text-cyan-600">{e.event_type.replace('xr:', '')}</span>
                          <span className="text-slate-500 truncate">{Object.entries(e.target || {}).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(' ')}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* v10.10.0 — Enviar notificación PWA al alumno XR */}
              <XRNotificationSender sessionId={activeSessionId} />

              {/* v10.11.0 — Modo espejo WebRTC */}
              <XRMirrorPanel sessionId={activeSessionId} />


              {/* Info del paciente */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Datos del Paciente</CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-1">
                  {sessionData.patient_area?.datos_paciente && (
                    <>
                      <p><strong>Nombre:</strong> {sessionData.patient_area.datos_paciente.nombre}</p>
                      <p><strong>Edad:</strong> {sessionData.patient_area.datos_paciente.edad} años</p>
                      <p><strong>Sexo:</strong> {sessionData.patient_area.datos_paciente.sexo}</p>
                      <p><strong>Motivo:</strong> {sessionData.patient_area.datos_paciente.motivo_consulta}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Área de Actividad del Estudiante */}
            <div className="col-span-5 overflow-auto">
              <Tabs defaultValue="timeline" className="h-full flex flex-col">
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="timeline">Cronología</TabsTrigger>
                  <TabsTrigger value="medications">Medicamentos</TabsTrigger>
                  <TabsTrigger value="exams">Exámenes</TabsTrigger>
                  <TabsTrigger value="actions">Acciones</TabsTrigger>
                </TabsList>
                
                <TabsContent value="timeline" className="flex-1 mt-4">
                  <ActivityTimeline
                    timeline={[...(wsTimeline || []), ...(sessionData.activity_area?.timeline || [])]}
                    studentActions={mergedActions}
                    medications={mergedMedications}
                    exams={mergedExams}
                  />
                </TabsContent>
                
                <TabsContent value="medications" className="flex-1 mt-4">
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Medicamentos Administrados</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px]">
                        {mergedMedications.length === 0 ? (
                          <p className="text-center text-gray-400 text-sm py-8">Sin medicamentos</p>
                        ) : (
                          <div className="space-y-2">
                            {mergedMedications.map((med, idx) => (
                              <div key={med.id || idx} className="p-2 bg-green-50 rounded border border-green-100 text-xs" data-testid={`sup-med-${idx}`}>
                                <p className="font-medium">{med.nombre || med.medication_name}</p>
                                <p className="text-gray-500">
                                  {med.dosis || med.dose} {med.unidad || med.unit || ''} · {med.via || med.route}
                                </p>
                                {med._source === 'ws_live' && (
                                  <p className="text-[10px] text-emerald-600 mt-0.5">⟳ En vivo · {med.actor || 'Alumno'}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="exams" className="flex-1 mt-4">
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Exámenes Solicitados</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px]">
                        {mergedExams.length === 0 ? (
                          <p className="text-center text-gray-400 text-sm py-8">Sin exámenes</p>
                        ) : (
                          <div className="space-y-2">
                            {mergedExams.map((exam, idx) => (
                              <div key={exam.id || idx} className="p-2 bg-blue-50 rounded border border-blue-100 text-xs">
                                <p className="font-medium">{exam.nombre || exam.name}</p>
                                <p className="text-gray-500">{exam.estado || 'Solicitado'}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="actions" className="flex-1 mt-4">
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Acciones Realizadas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px]">
                        {mergedActions.length === 0 ? (
                          <p className="text-center text-gray-400 text-sm py-8">Sin acciones</p>
                        ) : (
                          <div className="space-y-2">
                            {mergedActions.map((action, idx) => (
                              <div key={action.id || idx} className="p-2 bg-gray-50 rounded border text-xs" data-testid={`sup-action-${idx}`}>
                                <p className="font-medium">{action.nombre || action.name}</p>
                                <p className="text-gray-500">{action.categoria || action.category}</p>
                                {action._source === 'ws_live' && (
                                  <p className="text-[10px] text-emerald-600 mt-0.5">⟳ En vivo · {action.actor || 'Alumno'}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Área de Control y Evaluación */}
            <div className="col-span-4 space-y-4 overflow-auto">
              {/* Panel de comunicación */}
              <MessagingPanel
                sessionId={activeSessionId}
                messages={messages}
                hasControl={hasControl}
                supervisionMode={supervisionMode}
                onSendMessage={handleSendMessage}
                onSendHint={(content) => sendHint(content)}
              />
              
              {/* Eventos clínicos */}
              {(supervisionMode === 'asistido' || supervisionMode === 'control_total') && (
                <ClinicalEventsPanel
                  sessionId={activeSessionId}
                  scenarioId={sessionData.scenario?.id}
                  hasControl={hasControl}
                  onActivateEvent={handleActivateEvent}
                />
              )}
              
              {/* Evaluación */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Target className="w-4 h-4 text-green-500" />
                    Evaluación
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Objetivos completados:</span>
                      <span className="font-bold">
                        {sessionData.evaluation_area?.completed_objectives?.length || 0}/
                        {sessionData.evaluation_area?.scenario_objectives?.length || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Errores detectados:</span>
                      <span className="font-bold text-red-500">
                        {sessionData.evaluation_area?.errors_detected?.length || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Acciones totales:</span>
                      <span className="font-bold">
                        {sessionData.activity_area?.total_actions || 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista de Lista/Mosaico
  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Monitor className="w-6 h-6 text-blue-600" />
            Centro de Supervisión
          </h1>
          <p className="text-sm text-gray-600">
            {students.length} estudiantes autorizados
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Límites de supervisión */}
          {supervisionLimits && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border shadow-sm">
              <Users className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium">
                {supervisionLimits.is_unlimited ? (
                  <span className="text-green-600">Ilimitado</span>
                ) : (
                  <span className={supervisionLimits.percentage_used > 80 ? 'text-red-600' : ''}>
                    {supervisionLimits.current_count}/{supervisionLimits.max_simultaneous}
                  </span>
                )}
              </span>
            </div>
          )}
          
          {/* Historial */}
          <Button variant="outline" size="sm" onClick={() => navigate('/app/instructor/supervision-history')}>
            <History className="w-4 h-4 mr-1" />
            Historial
          </Button>
          
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar estudiante..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-[200px]"
            />
          </div>
          
          {/* Filtro de estado */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="en_simulacion">En Simulación</SelectItem>
              <SelectItem value="conectado">Conectado</SelectItem>
              <SelectItem value="esperando_inicio">Esperando</SelectItem>
              <SelectItem value="pausada">Pausada</SelectItem>
              <SelectItem value="desconectado">Desconectado</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Vista */}
          <div className="flex border rounded-lg">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'mosaic' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('mosaic')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Refrescar */}
          <Button variant="outline" size="sm" onClick={loadStudents}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Contenido */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : filteredStudents.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hay estudiantes
          </h3>
          <p className="text-gray-500">
            {searchQuery || statusFilter !== 'all'
              ? 'No se encontraron estudiantes con los filtros aplicados'
              : 'No tienes estudiantes autorizados para supervisar'}
          </p>
        </Card>
      ) : (
        <div className={`grid gap-4 ${
          viewMode === 'mosaic'
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3'
        }`}>
          {filteredStudents.map((student) => (
            <StudentCard
              key={student.id}
              student={student}
              isSelected={selectedStudent?.id === student.id}
              onSelect={setSelectedStudent}
              onSupervise={handleSupervise}
            />
          ))}
        </div>
      )}
    </div>
  );
}
